import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { Col, Collapse, Form, Row } from "react-bootstrap";
import { useOutletContext, useParams } from "react-router";
import {
  FieldSet,
  HorizontalDivider,
  FadedBackgroundDiv
} from "../common-styling";
import TextField from "../reusable-form-fields/text-field";
import { showError, showSuccess } from "../../services/toast-service";
import { AppointmentFormSchema } from "../../services/validation-schema";
import ActionButtonsColumn from "./integration-view/action-buttons-column";
import { handleCustomerAppointmentLink } from "../../services/api-declaration";
import IntegrationsTableView from "./integration-view/integrations-table-view";
import { useTranslation } from "react-i18next";
import RadioInput from "../reusable-form-fields/input-radio";
import { APPOINTMENT_TYPE } from "../strings";

// Up to three recipients of the appointment emails (Asana 1213561761053876)
const RECIPIENT_FIELDS = [
  "appointmentEmail1",
  "appointmentEmail2",
  "appointmentEmail3"
];
const toRecipientFields = (recipients = []) =>
  Object.fromEntries(
    RECIPIENT_FIELDS.map((field, idx) => [field, recipients[idx] || ""])
  );
const toRecipients = (formVals) => [
  ...new Set(
    RECIPIENT_FIELDS.map((field) =>
      (formVals[field] || "").trim().toLowerCase()
    ).filter(Boolean)
  )
];

const CustomerAppointment = () => {
  const { t } = useTranslation();
  const paramsId = useParams().id;
  const { funnel, setFunnel } = useOutletContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAllowedAddNew, setIsAllowedAddNew] = useState(false);
  const initialValues = {
    appointmentLink: "",
    appointmentConfirmation: "",
    appointmentType: "",
    // recipients stay stored when the link is deleted; offered again on re-adding
    ...toRecipientFields(funnel?.appointmentEmailRecipients)
  };
  const methods = useForm({
    resolver: yupResolver(AppointmentFormSchema),
    defaultValues: initialValues
  });
  const {
    reset,
    watch,
    register,
    handleSubmit,
    formState: { errors }
  } = methods;
  const confirmationSelected = watch("appointmentConfirmation") === "YES";

  const getFunnelAppointmentTypeLabel = (type) => {
    switch (type) {
      case APPOINTMENT_TYPE.ONSITE_AT_LEAD_ADDRESS:
        return t("appointmentTypes.onSiteAtLeadAddress");
      case APPOINTMENT_TYPE.ONSITE_AT_COMPANY_ADDRESS:
        return t("appointmentTypes.onSiteAtCompanyAddress");
      case APPOINTMENT_TYPE.PHONE_APPOINTMENT:
        return t("appointmentTypes.phoneAppointment");
      default:
        return "---";
    }
  };

  const dummyIntegration = funnel?.customerAppointmentLink
    ? [
        {
          appointmentLink: funnel.customerAppointmentLink,
          appointmentConfirmation: funnel.appointmentConfirmation
            ? t("funnelIntegration.appointment.yes")
            : t("funnelIntegration.appointment.no"),
          appointmentType: getFunnelAppointmentTypeLabel(
            funnel.appointmentType
          ),
          appointmentEmailRecipients: !funnel.appointmentConfirmation
            ? ""
            : funnel.appointmentEmailRecipients?.length
              ? funnel.appointmentEmailRecipients.join(", ")
              : t("funnelIntegration.appointment.recipientsFallback")
        }
      ]
    : [];

  const onSubmit = (formValues) => handleCustomerAppointment(formValues);
  const handleCustomerAppointment = async (formVals) => {
    try {
      const appointmentConfirmation =
        formVals.appointmentConfirmation === "YES";
      // with "No" (and when deleting the link) the stored recipients stay
      const appointmentEmailRecipients = appointmentConfirmation
        ? toRecipients(formVals)
        : undefined;
      const processedVals = {
        appointmentLink: formVals.appointmentLink,
        appointmentConfirmation,
        appointmentType: formVals.appointmentType,
        appointmentEmailRecipients
      };
      const response = await handleCustomerAppointmentLink(
        paramsId,
        processedVals
      );
      setFunnel({
        ...funnel,
        customerAppointmentLink: formVals.appointmentLink,
        appointmentConfirmation,
        appointmentType: formVals.appointmentType,
        ...(appointmentEmailRecipients && { appointmentEmailRecipients })
      });
      showSuccess(response?.data?.message || "success");
      resetComponentState();
    } catch (err) {
      showError(err.response?.data?.message || t("funnelIntegration.error"));
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleEditClick = () => {
    setIsAllowedAddNew(true);
    reset({
      appointmentLink: funnel?.customerAppointmentLink,
      appointmentConfirmation: funnel?.appointmentConfirmation ? "YES" : "NO",
      appointmentType: funnel?.appointmentType,
      ...toRecipientFields(funnel?.appointmentEmailRecipients)
    });
  };
  const resetComponentState = () => {
    reset(initialValues);
    setIsAllowedAddNew(false);
  };

  return (
    <FormProvider {...methods}>
      <Form
        className="p-0"
        onSubmit={(event) => {
          handleSubmit(onSubmit)(event);
        }}
      >
        <div className="px-3">
          <FieldSet>
            <Collapse in={isAllowedAddNew}>
              <div>
                <FadedBackgroundDiv className="p-2">
                  <Row className="mb-2">
                    <Col lg={10} md={8} sm={12}>
                      <TextField
                        type="text"
                        label={t("funnelIntegration.appointment.label")}
                        name="appointmentLink"
                        placeholder={t(
                          "funnelIntegration.appointment.placeholder"
                        )}
                        register={register}
                        errors={errors?.appointmentLink}
                        noSpacing
                      />
                    </Col>
                    <ActionButtonsColumn
                      mdColWidth={4}
                      fieldError={errors?.appointmentLink}
                      actions={{ isSubmitting, resetComponentState }}
                    />
                  </Row>
                  <RadioInput
                    label={t("funnelIntegration.appointment.confirm")}
                    firstOptLabel={t("funnelIntegration.appointment.yes")}
                    secondOptLabel={t("funnelIntegration.appointment.no")}
                    firstValue="YES"
                    firstValueId="YES"
                    secondValue="NO"
                    secondValueId="NO"
                    register={register}
                    name="appointmentConfirmation"
                    errors={errors?.appointmentConfirmation}
                    noSpacing={true}
                    className="mb-2"
                  />
                  {confirmationSelected && (
                    <div className="mb-3 ps-3 border-start">
                      <Row>
                        {RECIPIENT_FIELDS.map((field, idx) => (
                          <Col lg={4} md={12} key={field}>
                            <TextField
                              type="email"
                              label={`${t(
                                "funnelIntegration.appointment.recipient",
                                { number: idx + 1 }
                              )}${
                                idx
                                  ? ` ${t("funnelIntegration.appointment.optional")}`
                                  : ""
                              }`}
                              name={field}
                              placeholder={t(
                                "funnelIntegration.appointment.recipientPlaceholder"
                              )}
                              register={register}
                              errors={errors?.[field]}
                              noSpacing
                            />
                          </Col>
                        ))}
                      </Row>
                      <Form.Text muted>
                        {t("funnelIntegration.appointment.recipientsHint")}
                      </Form.Text>
                    </div>
                  )}
                  <RadioInput
                    label={t("leadAgent.appointmentType")}
                    firstOptLabel={t("appointmentTypes.onSiteAtLeadAddress")}
                    secondOptLabel={t(
                      "appointmentTypes.onSiteAtCompanyAddress"
                    )}
                    thirdOptLabel={t("appointmentTypes.phoneAppointment")}
                    firstValue={APPOINTMENT_TYPE.ONSITE_AT_LEAD_ADDRESS}
                    firstValueId={APPOINTMENT_TYPE.ONSITE_AT_LEAD_ADDRESS}
                    secondValue={APPOINTMENT_TYPE.ONSITE_AT_COMPANY_ADDRESS}
                    secondValueId={APPOINTMENT_TYPE.ONSITE_AT_COMPANY_ADDRESS}
                    thirdValue={APPOINTMENT_TYPE.PHONE_APPOINTMENT}
                    thirdValueId={APPOINTMENT_TYPE.PHONE_APPOINTMENT}
                    name="appointmentType"
                    errors={errors?.appointmentType}
                    register={register}
                    noSpacing
                    className="mb-3"
                  />
                </FadedBackgroundDiv>
                <HorizontalDivider />
              </div>
            </Collapse>
            <IntegrationsTableView
              headers={[
                {
                  name: t("funnelIntegration.appointment.label"),
                  colWidth: null,
                  style: "pb-1"
                },
                {
                  name: t("funnelIntegration.appointment.confirm"),
                  colWidth: null,
                  style: "pb-1"
                },
                {
                  name: t("funnelIntegration.appointment.type"),
                  colWidth: null,
                  style: "pb-1"
                },
                {
                  name: t("funnelIntegration.appointment.recipients"),
                  colWidth: null,
                  style: "pb-1"
                },
                {
                  name: "Action",
                  colWidth: 2,
                  style: "pb-1 text-end"
                }
              ]}
              integrations={dummyIntegration}
              propertiesToRender={[
                "appointmentLink",
                "appointmentConfirmation",
                "appointmentType",
                "appointmentEmailRecipients"
              ]}
              onEditClick={handleEditClick}
              isAllowedAddNew={isAllowedAddNew}
              setIsAllowedAddNew={setIsAllowedAddNew}
              handleCustomerAppointment={handleCustomerAppointment}
              forCustomerAppointment={Boolean(dummyIntegration.length)}
              resetComponentState={resetComponentState}
            />
          </FieldSet>
        </div>
      </Form>
    </FormProvider>
  );
};

export default CustomerAppointment;
